import { useState } from 'react';
import { Carousel, Col, Container, Image, Row } from 'react-bootstrap';
import { PageMeta } from '../../../components/meta/PageMeta';
import { StickySubNav } from '../../../components/nav/StickySubNav';
import { BannerHeader } from '../../user/BannerHeader';
import { useGetGalleryQuery } from '../../../services/endpoints/website/gallery';
import { getPartition } from '../../../utils/utils';
import Dots from '../../../assets/images/sfc-dots.png';
import img03 from '../../../assets/images/privateEvents/private-team-building-cooking-class_sticky-fingers-cooking_03.jpg';
import img02 from '../../../assets/images/privateEvents/private-team-building-cooking-class_sticky-fingers-cooking_02.jpg';
import imgAig from '../../../assets/images/privateEvents/aig-private-team-building-cooking-class_sticky-fingers-cooking.jpg';
import img04 from '../../../assets/images/privateEvents/private-team-building-cooking-class_sticky-fingers-cooking_04.jpg';
import './PrivateEvents.css';

const NAV_ITEMS = [
  { label: 'Online Team Building Cooking Classes', id: 'online-team-building-classes' },
  { label: 'Class Details', id: 'online-corporate-event-details' },
  { label: 'Testimonials', id: 'team-building-classes-testimonials' },
  { label: 'Gallery', id: 'corporate-event-gallery' },
];

const TESTIMONIALS = [
  { quote: "I was so impressed with your mission, curriculum, and the overall sophistication of your creation.", author: "Kimberly R., Vitamin Cottage Natural Food Markets, Inc." },
  { quote: "Thank you so much for everything. Very professional, patient, kind, encouraging. We had a blast!", author: "Chaznae W., I Have A Dream Foundation" },
  { quote: "We all really enjoyed our cooking class series.", author: "Susan M., AIG Private Client Group" },
  { quote: "Let's sign up for another class!", author: "Elise L., Thrivent Financial" },
  { quote: "Your recipes are amazing! We were oohing and ahhing. Our whole team is truly thrilled!!!", author: "Emily W., Spicewalla Brand" },
  { quote: "I see Sticky Fingers Cooking as a valuable brand asset. I've got your program top of mind!", author: "Vicki H., Hidden Valley Ranch" },
];

const CorporateEvents: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { data: galleryData } = useGetGalleryQuery(5);

  return (
      <>
        <PageMeta
            title="Virtual Team Building Cooking Events - Sticky Fingers Cooking"
            description="Treat your staff to a unique team building experience with Sticky Fingers Cooking! Corporate cooking classes and events online or in-person."
        />
        <BannerHeader title="Corporate & Team Building Events" />

        <StickySubNav items={NAV_ITEMS} />

        {/* Intro Section */}
        <div id="online-team-building-classes" className="section-events-child">
          <Container>
            <div className="container-flex">
              <div className="div-block-events-large">
                <h2>Online Team Building Cooking Classes</h2>
                <Image className="w-100 dots-divider my-2" src={Dots} />
                <p>Sticky Fingers Cooking offers fun, inspired, and uniquely tasty ways to build culture in remote teams.</p>
                <p>Looking for a way to SOUP up your staff meetings? We've got EGGS-actly what you need! Break bread and stay connected with your colleagues, staff, friends, and families through a group online cooking experience. <strong>Foster better working relationships, while cooking up connectivity.</strong></p>
                <p>Now more than ever there is a growing need to come together, eat well, and have fun! Why not cook together and meet each other's families?</p>
                <p>Our corporate, family-focused, online cooking classes bring our talented, dedicated, and experienced crew of chef instructors directly to your remote working employees for an unforgettable team building experience. We can also give your hard working team members a break and cook up some fun with just their kiddos! Either way, you and your staff ends up with a delectable dish and a shared bonding experience that the entire company is bound to enjoy!</p>
                <a href="/contact" className="btn-events">Book a Corporate Event</a>
              </div>
              <div className="div-block-events-small">
                <div className="div-image">
                  <img src={img03} alt="Family enjoying a Sticky Fingers private online cooking class" className="image-events" />
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Details Section */}
        <div id="online-corporate-event-details" className="section-events-child alt">
          <Container>
            <div className="container-flex">
              <div className="div-block-events-large">
                <h2>Online Corporate Event Details</h2>
                <Image className="w-100 dots-divider my-2" src={Dots} />
                <p>When it comes to our online team building cooking classes, <strong>scheduling is flexible!</strong> We provide all the logistics and support, customizing these cooking classes for your team's wants and needs. Video conferencing is integrated on our website. Easy, safe and secure! However, we can also host your event via the platform of your choice.</p>
                <p>Our curriculum can easily include additional information about your company or initiatives; we are happy to work with you to accommodate special content requests.</p>
                <p>All of our instructors have completed background checks and fingerprinting and have worker's comp and insurance.</p>
                <h3>Swag Bags</h3>
                <p>We can customize any of our classes to include hand selected swag for your crew! Our most popular items are:</p>
                <ul className="w-list-unstyled">
                  <li>Chef hats</li>
                  <li>Mugs</li>
                  <li>Kid-friendly learning knives</li>
                  <li>Custom embroidered kid aprons with your company's logo!</li>
                </ul>
                <h3>Recipes</h3>
                <p>We provide custom recipe collections with your organization's logo. Recipes include shopping lists and equipment checklists. You choose the recipe from our extensive recipe library. Or choose a specialty theme such as Cooks + Books Nourishing Literature Classes, Specialty 101 Skills Classes, Holiday Themed Classes, or Food as Gifts Classes.</p>
                <ul className="w-list-unstyled">
                  <li>Magical Middle Eastern Shakshuka Poached Eggs + Pronto Pita Bread + Pineapple Orange Spritzers</li>
                  <li>Spanish Empanada de Manzana (apple pies) + Sorbete de Manzana</li>
                  <li>Mini Mahogany Cakes + Cheery Cherry Chocolate Drizzle + Iced Cherry Vanilla Spritzers</li>
                  <li>Lucky Lo Mein Noodles + Crazy Delicious Caramelized Cabbage + Chinese Sweet Soy Splash + Green Tea Smoothies</li>
                  <li>Chef Shepherd's Pie + Fresh Spinach Salad + Emerald Ranch Dressing + Lucky Limeade</li>
                  <li>Positively Pumpkin Gnocchi + Whipped Sage Butter + Pumpkin Pie Shakes</li>
                </ul>
                <p>Looking for a particular recipe? Craving a type of cuisine? Just ask us! Please note, we accommodate ANY and ALL dietary restrictions or allergies.</p>
                <h3>Pricing</h3>
                <p>Pricing ranges from $20–45 per household for a 1-hour class. Pricing depends on the number of participants, length of class, and contents of the swag bag. Bulk discounts available.</p>
                <a href="/contact" className="btn-events">Book Your Event</a>
              </div>
              <div className="div-block-events-small">
                <div className="div-image">
                  <img src={img02} alt="Family enjoying a Sticky Fingers private online cooking class" className="image-events" />
                </div>
                <div className="div-image">
                  <img src={imgAig} alt="Custom apron and chef hat provided by Sticky Fingers Cooking" className="image-events rotate-left" />
                </div>
                <div className="div-image">
                  <img src={img04} alt="Brother and sister enjoying a Sticky Fingers private online cooking class" className="image-events" />
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Testimonials */}
        <section id="team-building-classes-testimonials" className="section-testimonial-slider">
          <Container>
            <Row>
              <Col lg={7}>
                <Carousel
                    controls={false}
                    indicators={false}
                    interval={4500}
                    activeIndex={activeIndex}
                    onSelect={(i) => setActiveIndex(i)}
                >
                  {TESTIMONIALS.map((t, i) => (
                      <Carousel.Item key={i}>
                        <div className="text-quote">"{t.quote}"</div>
                        <div className="subheader white">{t.author}</div>
                      </Carousel.Item>
                  ))}
                </Carousel>
              </Col>
            </Row>
            <Row>
              <Col lg={7}>
                <div className="testimonial-dots">
                  {TESTIMONIALS.map((_, i) => (
                      <button
                          key={i}
                          className={`testimonial-dot${activeIndex === i ? ' active' : ''}`}
                          onClick={() => setActiveIndex(i)}
                      />
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Gallery */}
        {galleryData?.gallery?.pictures && galleryData.gallery.pictures.length > 0 && (
            <div id="corporate-event-gallery" className="section-events-child">
              <Container>
                <div className="photo-grid">
                  {galleryData.gallery.pictures.map((pic, i) => (
                      <div key={i} className="photo-grid-item">
                        <img
                            src={`${pic.s3_base_url}/${getPartition(pic.id)}/original/${pic.image_file_name}`}
                            alt={pic.name ?? `Corporate team building event ${i + 1}`}
                        />
                      </div>
                  ))}
                </div>
              </Container>
            </div>
        )}
      </>
  );
};

export default CorporateEvents;